import {
  DAY_SECONDS,
  className,
  edgeNameOf,
  formatClock,
  type County,
  type DetectionRow,
  type JourneyPoint,
} from '@hotlist/shared';

/**
 * The patrol laptop.
 *
 * DESIGN.md section 4: enforcement plays from a mobile data terminal, and
 * everything that side can do is done through one surface, so the power and the
 * friction are visible in the same place.
 *
 * What it is, structurally, is a text box over a database. That is not a
 * shortcut, it is section 3: the game's information layer *is* a table, queried
 * the way the real one is queried, and dressing it up as anything else would be
 * the one place the game lied about its subject.
 *
 * What it does not have is a field for why you are asking. That hole is
 * deliberate and it is the whole of M4.
 */

export interface QueryPanelHandlers {
  search: (by: 'plate' | 'camera', term: string, reason: string) => void;
  /** Trace a searched plate's journey -- its reads -- on the map. */
  trace: (points: JourneyPoint[]) => void;
  /** Show the traffic heatmap for this officer's cameras. */
  heatmap: () => void;
}

export class QueryPanel {
  private readonly form: HTMLFormElement;
  private readonly input: HTMLInputElement;
  private readonly mode: HTMLSelectElement;
  private readonly status: HTMLElement;
  private readonly results: HTMLElement;
  private readonly county: County;
  private readonly reason: HTMLInputElement;
  private readonly scope: HTMLElement;
  private posting: { agency: string; agencyCameras: number; countyCameras: number } | null = null;
  private sharing = false;
  private daySeconds = DAY_SECONDS;
  private open = false;
  private readonly traceBtn: HTMLButtonElement;
  private readonly heatBtn: HTMLButtonElement;
  /** The last plate search's reads, held so "trace on map" can draw them. */
  private lastPlateRows: DetectionRow[] = [];

  constructor(county: County, handlers: QueryPanelHandlers) {
    this.county = county;
    this.form = must('query-form') as HTMLFormElement;
    this.input = must('query-term') as HTMLInputElement;
    this.mode = must('query-mode') as HTMLSelectElement;
    this.reason = must('query-reason') as HTMLInputElement;
    this.scope = must('query-scope');
    this.status = must('query-status');
    this.results = must('query-results');
    this.traceBtn = must('query-trace') as HTMLButtonElement;
    this.heatBtn = must('query-heatmap') as HTMLButtonElement;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      const term = this.input.value.trim();
      if (!term) return;
      this.status.textContent = 'searching';
      // Sent as typed, including empty. The field is never cleared for the
      // player and never filled in for them: what goes in the log is what they
      // put there, and most of the time that will be nothing.
      handlers.search(this.mode.value as 'plate' | 'camera', term, this.reason.value.trim());
    });

    this.heatBtn.addEventListener('click', () => handlers.heatmap());
    this.traceBtn.addEventListener('click', () => {
      if (this.lastPlateRows.length === 0) return;
      handlers.trace(
        this.lastPlateRows.map((r) => ({
          n: r.n,
          at: r.at,
          mph: Math.round(r.s * 2.23694),
          b: r.b,
        })),
      );
    });
  }

  /** The county's day, so timestamps agree with the world rather than with 86,400. */
  setDaySeconds(seconds: number): void {
    if (Number.isFinite(seconds) && seconds > 0) this.daySeconds = seconds;
  }

  /** Which department this console belongs to, and how small it is. */
  setPosting(posting: { agency: string; agencyCameras: number; countyCameras: number }): void {
    this.posting = posting;
    this.paintScope();
  }

  /** Whether the shared network is switched on. */
  setSharing(on: boolean): void {
    this.sharing = on;
    this.paintScope();
  }

  /**
   * The line that says whose cameras this console is looking through.
   *
   * Always visible, because the scope of a search is the least obvious and most
   * consequential thing about it: the same query against nine cameras and
   * against two hundred and thirty-seven is the same query, and only this line
   * says which one just happened.
   */
  private paintScope(): void {
    if (!this.posting) return;
    const { agency, agencyCameras, countyCameras } = this.posting;
    this.scope.textContent = this.sharing
      ? `${agency} — searching all ${countyCameras} cameras in the county`
      : `${agency} — searching only its own ${agencyCameras} cameras. Turn on the shared network for all ${countyCameras}.`;
    this.scope.classList.toggle('query-scope-shared', this.sharing);
  }

  /**
   * Marks this panel live. Visibility belongs to the console, which owns the
   * tabs: two things deciding whether one element is on screen is how a panel
   * ends up showing through the tab that is supposed to be covering it.
   */
  enable(): void {
    this.open = true;
  }

  get enabled(): boolean {
    return this.open;
  }

  /** Put a plate in the box, which is what clicking a car you can read does. */
  prefill(plate: string): void {
    if (!this.open) return;
    this.mode.value = 'plate';
    this.input.value = plate;
    this.input.focus();
    this.input.select();
  }

  fail(message: string): void {
    this.status.textContent = message;
  }

  show(by: 'plate' | 'camera', term: string, rows: DetectionRow[], truncated: boolean, millis: number): void {
    // How many different states issued the serial that was searched.
    //
    // A search matches six characters. Two states issue the same six to
    // different cars, so an answer can be two lives interleaved, sorted by
    // time, looking exactly like one car doing something inexplicable.
    //
    // The count is on the status line because the alternative is that the
    // player has to notice it themselves, and the entire finding here is that
    // people do not notice things they were not asked to look at. Making it
    // legible is not making it automatic: nothing filters, nothing refuses to
    // answer, and the two lives stay in one list until somebody separates them.
    const states = new Set(rows.map((r) => r.j).filter((j) => j !== ''));
    const mixed = by === 'plate' && states.size > 1;

    // A plate search's reads are a journey: hold them so the officer can trace
    // it on the map, the same reconstruction the record is really for.
    this.lastPlateRows = by === 'plate' ? rows : [];
    this.traceBtn.hidden = !(by === 'plate' && rows.length > 0);

    this.status.textContent = rows.length
      ? `${rows.length}${truncated ? '+' : ''} reads in ${millis} ms` +
        (mixed ? ` — ${states.size} states issued ${term}` : '')
      : `nothing on ${term} in ${millis} ms`;
    this.status.classList.toggle('query-mixed', mixed);

    this.results.replaceChildren();
    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'query-empty';
      // The honest thing to say. A plate with no reads has not been ruled out;
      // it has been not seen, which is a different fact and the one the whole
      // archive is about.
      empty.textContent =
        by === 'plate'
          ? 'No reads.'
          : 'Nothing in that window.';
      this.results.append(empty);
      return;
    }

    // Newest first, and grouped by how long since the one before it, because
    // the gaps are what a person actually reads off a list like this.
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] as DetectionRow;
      const previous = rows[i - 1] as DetectionRow | undefined;

      const line = document.createElement('div');
      line.className = 'query-row';

      const when = document.createElement('span');
      when.className = 'query-when';
      when.textContent = formatClock(row.at, this.daySeconds);

      const what = document.createElement('span');
      what.className = 'query-what';
      what.textContent = by === 'plate' ? placeOf(this.county, row.n) : row.p;

      // Always shown, not only when it disagrees. A column that appears the
      // moment something is wrong is a warning; a column that is always there
      // is a record, and the point is that the record was always sufficient.
      const juris = document.createElement('span');
      juris.className = mixed ? 'query-juris query-juris-split' : 'query-juris';
      juris.textContent = row.j;

      const detail = document.createElement('span');
      detail.className = 'query-detail';
      detail.textContent = `${compass(row.b)} ${Math.round(row.s * 2.23694)} mph`;

      line.append(when, juris, what, detail);
      this.results.append(line);

      if (previous) {
        const gap = previous.at - row.at;
        if (gap > 60) {
          const between = document.createElement('div');
          between.className = 'query-gap';
          between.textContent = `${duration(gap)} unaccounted for`;
          this.results.append(between);
        }
      }
    }
  }
}

/** Where a junction is, in words somebody could act on. */
function placeOf(county: County, node: number): string {
  if (node < 0 || node >= county.nodeCount) return 'unknown';
  const start = county.adjStart[node] as number;
  const end = county.adjStart[node + 1] as number;

  const names: string[] = [];
  let best = '';
  for (let i = start; i < end; i++) {
    const edge = county.adjEdge[i] as number;
    const name = edgeNameOf(county, edge);
    if (name && !names.includes(name)) names.push(name);
    const cls = className(county.edgeClass[edge] as number);
    if (!best && (cls === 'motorway' || cls === 'trunk' || cls === 'primary')) best = name;
  }

  if (names.length >= 2) return `${names[0]} at ${names[1]}`;
  if (names.length === 1) return names[0] as string;
  return best || `junction ${node}`;
}

function duration(seconds: number): string {
  const total = Math.round(seconds);
  if (total < 3600) return `${Math.round(total / 60)} min`;
  if (total < 86400) return `${(total / 3600).toFixed(1)} h`;
  return `${(total / 86400).toFixed(1)} days`;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

function compass(bearing: number): string {
  return COMPASS[Math.round(bearing / 45) % 8] as string;
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
